const closeHeaderWooDetails = (details, focusSummary) => {
  if (!details.hasAttribute('open')) return;
  details.removeAttribute('open');
  if (!focusSummary) return;
  const summary = details.querySelector('summary');
  if (summary) summary.focus();
};

const focusableIn = (root) =>
  Array.from(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')).filter(
    (el) => el.offsetParent !== null
  );

// Everything outside `keep`, walking up to <body>, so the open panel is the only interactive content.
const inertOutside = (keep) => {
  const nodes = [];
  for (let node = keep; node.parentElement && node !== document.body; node = node.parentElement) {
    Array.from(node.parentElement.children).forEach((sibling) => {
      if (sibling === node || sibling.inert || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;
      sibling.inert = true;
      nodes.push(sibling);
    });
  }
  return () => nodes.forEach((sibling) => (sibling.inert = false));
};

const initSubmenu = (sub, signal) => {
  const toggle = sub.querySelector('.header-woo__sub-toggle');
  const submenu = sub.querySelector('.header-woo__submenu');
  if (!toggle || !submenu) return;

  const isOpen = () =>
    !sub.classList.contains('is-dismissed') &&
    (sub.classList.contains('is-open') || sub.matches(':hover') || sub.matches(':focus-within'));
  const sync = () => toggle.setAttribute('aria-expanded', String(isOpen()));
  const close = () => {
    sub.classList.remove('is-open');
    sub.classList.add('is-dismissed');
    sync();
  };

  // A tap or click also triggers :hover / :focus-within, so pointer clicks toggle on the class alone.
  toggle.addEventListener('click', (event) => {
    if (sub.classList.contains('is-open') || (event.detail === 0 && isOpen())) return close();
    sub.classList.remove('is-dismissed');
    sub.classList.add('is-open');
    sync();
  });
  sub.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !isOpen()) return;
    if (submenu.contains(document.activeElement)) toggle.focus();
    close();
  });
  sub.addEventListener('mouseenter', sync);
  sub.addEventListener('focusin', sync);
  sub.addEventListener('mouseleave', () => {
    if (!sub.matches(':focus-within')) sub.classList.remove('is-dismissed');
    sync();
  });
  sub.addEventListener('focusout', (event) => {
    if (sub.contains(event.relatedTarget)) return;
    sub.classList.remove('is-open', 'is-dismissed');
    requestAnimationFrame(sync);
  });
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape' && sub.matches(':hover') && isOpen()) close();
    },
    { signal }
  );
  document.addEventListener(
    'click',
    (event) => {
      if (sub.contains(event.target) || !sub.classList.contains('is-open')) return;
      sub.classList.remove('is-open');
      sync();
    },
    { signal }
  );
};

const initMobileMenu = (mobileMenu, signal) => {
  const summary = mobileMenu.querySelector('summary');
  const panel = mobileMenu.querySelector('.header-woo__mobile-panel');
  const closeButton = mobileMenu.querySelector('.header-woo__mobile-head button');
  let bodyOverflow = '';
  let releaseInert = null;

  mobileMenu.addEventListener('toggle', () => {
    const open = mobileMenu.hasAttribute('open');
    summary.setAttribute('aria-expanded', String(open));
    if (open) {
      bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      releaseInert = inertOutside(panel);
      (closeButton || focusableIn(panel)[0]).focus();
      return;
    }
    document.body.style.overflow = bodyOverflow;
    if (releaseInert) releaseInert();
    releaseInert = null;
    if (!document.activeElement || document.activeElement === document.body || panel.contains(document.activeElement)) {
      summary.focus();
    }
  });

  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const items = focusableIn(panel);
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  if (closeButton) closeButton.addEventListener('click', () => closeHeaderWooDetails(mobileMenu, true));

  // The burger is hidden from 880px, so an open menu would leave the page inert after a rotate or resize.
  const desktop = window.matchMedia('(min-width: 880px)');
  desktop.addEventListener('change', () => desktop.matches && closeHeaderWooDetails(mobileMenu, false), { signal });
};

const initHeaderWoo = () => {
  const header = document.querySelector('.header-woo');
  if (!header || header.dataset.wooInit) return;
  header.dataset.wooInit = '1';
  const controller = new AbortController();
  const { signal } = controller;
  document.addEventListener(
    'shopify:section:unload',
    (event) => event.target.contains(header) && controller.abort(),
    { signal }
  );

  const toggles = header.querySelectorAll('.header-woo__mobile, .header-woo__search');

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      toggles.forEach((details) => closeHeaderWooDetails(details, true));
    },
    { signal }
  );

  document.addEventListener(
    'click',
    (event) => {
      toggles.forEach((details) => {
        if (details.hasAttribute('open') && !details.contains(event.target)) {
          closeHeaderWooDetails(details, false);
        }
      });
    },
    { signal }
  );

  header.querySelectorAll('.header-woo__has-sub').forEach((sub) => initSubmenu(sub, signal));
  const mobileMenu = header.querySelector('.header-woo__mobile');
  if (mobileMenu) initMobileMenu(mobileMenu, signal);
};

if (document.readyState !== 'loading') initHeaderWoo();
else document.addEventListener('DOMContentLoaded', initHeaderWoo);
document.addEventListener('shopify:section:load', initHeaderWoo);
